import { clearCsrfToken, setCsrfToken } from './csrf';
import type { UnauthorizedTransport } from './contract-client';
import { ApiError } from './errors';
import type { AuthGateway, AuthenticationResponse, LoginRequest } from './provider';

type AuthenticatedUser = AuthenticationResponse['user'];

export type SessionSnapshot =
  | {
      readonly status: 'initializing';
      readonly user: null;
      readonly error: null;
    }
  | {
      readonly status: 'authenticated';
      readonly user: AuthenticatedUser;
      readonly error: null;
    }
  | {
      readonly status: 'anonymous';
      readonly user: null;
      readonly error: null;
    }
  | {
      readonly status: 'unavailable';
      readonly user: null;
      readonly error: Error;
    };

export interface SessionController {
  readonly getSnapshot: () => SessionSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  readonly bootstrap: () => Promise<void>;
  readonly retry: () => Promise<void>;
  readonly login: (input: LoginRequest) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly handleUnauthorized: () => void;
}

export interface UnauthorizedBridge {
  readonly advanceEpoch: () => number;
  readonly subscribe: (listener: () => void) => () => void;
  readonly transport: UnauthorizedTransport;
}

type SessionEvent =
  | { readonly type: 'initialize' }
  | { readonly type: 'authenticate'; readonly user: AuthenticatedUser }
  | { readonly type: 'anonymize' }
  | { readonly type: 'fail'; readonly error: Error };

const INITIALIZING: SessionSnapshot = Object.freeze({
  status: 'initializing',
  user: null,
  error: null,
});

const ANONYMOUS: SessionSnapshot = Object.freeze({
  status: 'anonymous',
  user: null,
  error: null,
});

export function createUnauthorizedBridge(): UnauthorizedBridge {
  const listeners = new Set<() => void>();
  let epoch = 0;

  const advanceEpoch = () => {
    epoch += 1;
    return epoch;
  };

  return {
    advanceEpoch,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    transport: {
      capture: () => ({ epoch }),
      notify(context) {
        if (context.epoch !== epoch) return;
        listeners.forEach((listener) => listener());
      },
    },
  };
}

export function createSessionController(
  auth: AuthGateway,
  unauthorizedBridge?: UnauthorizedBridge,
): SessionController {
  const listeners = new Set<() => void>();
  let snapshot: SessionSnapshot = INITIALIZING;
  let bootstrapStarted = false;
  let bootstrapRequest: Promise<void> | null = null;
  let loginRequest: Promise<void> | null = null;
  let logoutRequest: Promise<void> | null = null;
  let operationVersion = 0;

  const dispatch = (event: SessionEvent) => {
    snapshot = reduceSession(snapshot, event);
    listeners.forEach((listener) => listener());
  };

  const publishAuthentication = (response: AuthenticationResponse, version: number) => {
    if (operationVersion !== version) return;

    unauthorizedBridge?.advanceEpoch();
    setCsrfToken(response.csrf_token);
    dispatch({ type: 'authenticate', user: response.user });
  };

  const handleUnauthorized = () => {
    unauthorizedBridge?.advanceEpoch();
    operationVersion += 1;
    clearCsrfToken();
    dispatch({ type: 'anonymize' });
  };

  const beginSessionOperation = () => {
    unauthorizedBridge?.advanceEpoch();
    operationVersion += 1;
    return operationVersion;
  };

  const runBootstrap = (force: boolean): Promise<void> => {
    if (bootstrapRequest) return bootstrapRequest;
    if (!force && bootstrapStarted) return Promise.resolve();

    bootstrapStarted = true;
    const version = beginSessionOperation();
    clearCsrfToken();
    if (force || snapshot.status !== 'initializing') dispatch({ type: 'initialize' });

    const request = (async () => {
      try {
        const response = await auth.me();
        publishAuthentication(response, version);
      } catch (error) {
        if (operationVersion !== version) return;

        clearCsrfToken();
        if (isAuthenticationRequired(error)) {
          dispatch({ type: 'anonymize' });
          return;
        }
        dispatch({ type: 'fail', error: toError(error) });
      }
    })();

    bootstrapRequest = request;
    const clearBootstrapRequest = () => {
      if (bootstrapRequest === request) bootstrapRequest = null;
    };
    void request.then(clearBootstrapRequest, clearBootstrapRequest);
    return request;
  };

  const login = (input: LoginRequest): Promise<void> => {
    if (loginRequest) return loginRequest;

    const version = beginSessionOperation();
    const request = (async () => {
      try {
        const response = await auth.login(input);
        publishAuthentication(response, version);
      } catch (error) {
        if (operationVersion === version) {
          clearCsrfToken();
          dispatch({ type: 'anonymize' });
        }
        throw error;
      }
    })();

    loginRequest = request;
    const clearLoginRequest = () => {
      if (loginRequest === request) loginRequest = null;
    };
    void request.then(clearLoginRequest, clearLoginRequest);
    return request;
  };

  const logout = (): Promise<void> => {
    if (logoutRequest) return logoutRequest;

    beginSessionOperation();
    const request = (async () => {
      try {
        await auth.logout();
      } finally {
        handleUnauthorized();
      }
    })();

    logoutRequest = request;
    const clearLogoutRequest = () => {
      if (logoutRequest === request) logoutRequest = null;
    };
    void request.then(clearLogoutRequest, clearLogoutRequest);
    return request;
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    bootstrap: () => runBootstrap(false),
    retry: () => runBootstrap(true),
    login,
    logout,
    handleUnauthorized,
  };
}

function reduceSession(snapshot: SessionSnapshot, event: SessionEvent): SessionSnapshot {
  switch (event.type) {
    case 'initialize':
      return INITIALIZING;
    case 'authenticate':
      return {
        status: 'authenticated',
        user: event.user,
        error: null,
      };
    case 'anonymize':
      return ANONYMOUS;
    case 'fail':
      return {
        status: 'unavailable',
        user: null,
        error: event.error,
      };
    default:
      return snapshot;
  }
}

function isAuthenticationRequired(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 401 && error.code === 'authentication_required'
  );
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Unknown session error');
}
